import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Send, MessageSquare } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, formatDateTime } from "@/components/admin/ui";
import { CustomerPickerField } from "./CustomerPickerField";

export const CommunicationTab = ({ API, canManage }) => {
  const [customer, setCustomer] = useState(null);
  const [channels, setChannels] = useState(null);
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const [channel, setChannel] = useState("portal");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/org/support/meta`, { withCredentials: true }).then(({ data }) => setChannels(data.channels)).catch(() => {});
  }, [API]);

  const loadThread = useCallback(() => {
    if (!customer) return;
    axios.get(`${API}/org/support/communications`, { params: { customer_id: customer.customer_id }, withCredentials: true })
      .then(({ data }) => setThread(data.communications)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, customer]);

  useEffect(() => { setThread(null); loadThread(); }, [loadThread]);

  const send = async () => {
    if (!customer || !body.trim()) return;
    setSending(true); setError("");
    try {
      await axios.post(`${API}/org/support/communications`, { customer_id: customer.customer_id, channel, subject: subject.trim() || null, body: body.trim() }, { withCredentials: true });
      setBody(""); setSubject("");
      loadThread();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <Panel title="Communication" subtitle="Full message history with a customer, across Portal and Email." testId="support-communication-tab">
      <div className="mb-4 max-w-md"><CustomerPickerField API={API} value={customer} onChange={setCustomer} testId="support-communication-customer-picker" /></div>
      {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="support-communication-error">{error}</p>}

      {!customer ? (
        <EmptyState icon={MessageSquare} title="Select a customer to view their message history." testId="support-communication-no-customer" />
      ) : (
        <div className="space-y-4">
          <div className="max-h-[360px] space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid="support-communication-thread">
            {!thread ? <p className="text-sm text-slate-400">Loading…</p> : thread.length === 0 ? (
              <p className="text-sm text-slate-400">No messages with this customer yet.</p>
            ) : thread.map((m) => (
              <div key={m.communication_id} className={`max-w-[85%] rounded-lg border p-2.5 text-sm ${m.direction === "inbound" ? "border-slate-200 dark:border-white/10" : "ml-auto border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10"}`} data-testid={`support-communication-msg-${m.communication_id}`}>
                {m.subject && <p className="font-bold text-slate-900 dark:text-white">{m.subject}</p>}
                <p className="text-slate-900 dark:text-white">{m.body}</p>
                <p className="mt-1 text-xs text-slate-400">{m.direction === "inbound" ? "Customer" : "Staff"} · {m.channel}{m.status === "failed" ? " · Failed to send" : ""} · {formatDateTime(m.created_at)}</p>
              </div>
            ))}
          </div>

          {canManage && (
            <div className="space-y-2 rounded-xl border border-dashed border-slate-300 p-4 dark:border-white/20" data-testid="support-communication-compose">
              <div className="flex gap-2">
                <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="support-communication-channel-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                  <option value="portal">Customer Portal</option>
                  <option value="email" disabled={!channels?.email}>{channels?.email ? "Email" : "Email (not connected)"}</option>
                  <option value="sms" disabled>SMS (not available yet)</option>
                </select>
                {channel === "email" && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="support-communication-subject-input" className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />}
              </div>
              <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message…" data-testid="support-communication-body-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <div className="flex justify-end">
                <button type="button" onClick={send} disabled={sending || !body.trim()} data-testid="support-communication-send-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
                  <Send size={13} /> Send
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};
